/**
 * Which form a location code belongs to. Spec 007 D-3 and FR-704.
 *
 * ── WHAT HAPPENED ───────────────────────────────────────────────────────────
 *
 * The Locations screen has two forms. "Add a row of racking" makes real rack
 * codes from parts: building, row, bay, shelf, tub, compartment, joined as
 * `01-A-01-A-01-A`. "Add a location" is free text, and it is older than racking
 * by months. Its hints read "BAY-3, STORE, RACK-A1" and "Second rack, top
 * shelf", which describe racking in every word, and it has no Row field at all.
 *
 * So the free-text form looked like the way to add a shelf, and it was not. A
 * location made by it has no building and no row, which means the rack map
 * cannot draw it: the map is built from rows. It appears nowhere and the person
 * concludes nothing happened.
 *
 * The client database carries the proof. There is a live location with the code
 * `BAY-1`, the zone `SHELF-D` and the label "Row F, bay 01, shelf D, tub 03,
 * compartment A". Somebody typed a rack position into the only form that looked
 * like it would take one. Guy then wrote tracker #5: "the Stock Locations Add
 * field doesn't line up with the names of the actual locations them selves, I
 * feel we are missing the Row".
 *
 * He is right, and the answer is not to add a Row box to this form. Two forms
 * that both make rack locations is how two codes for one shelf appear. The
 * answer is that this form refuses a rack position and says which form takes
 * one.
 *
 * WHAT COUNTS AS A RACK CODE, and why each shape is here:
 *
 *  - `01-A-01-A-01-A`, and any leading part of it. This is what the rack form
 *    makes. Two digits, then a row letter.
 *  - `BAY-1`, `BAY 3`, `BAY3`. The live stray, and the old hint that invited
 *    it.
 *  - `ROW-A`, `SHELF-D`, `TUB-02`, `RACK-A1`. The same mistake in the other
 *    words a person standing in the store room would use.
 *
 * WHAT IS STILL ALLOWED, because these are real places that are not racking:
 * `PACKING-BENCH`, `CUTTING-TABLE`, `DELIVERY-CAGE`, `VAN`, `STORE`, and the
 * two imported Shopify rows.
 */

/** The words a rack position is made of, whichever way a person writes it. */
const RACK_WORDS = ['BAY', 'ROW', 'SHELF', 'TUB', 'RACK', 'COMPARTMENT'];

/**
 * True when this code names a position on the racking.
 *
 * Case and surrounding space do not matter: the handler upper-cases before it
 * stores, so the check has to see the same thing the store would.
 */
export function looksLikeRackCode(code: string): boolean {
  const c = code.trim().toUpperCase();
  if (!c) return false;

  // What the racking form itself makes: two digits, a hyphen, a row letter.
  // `01-A`, `01-A-01`, `01-A-01-A-01-A`.
  if (/^\d{2}-[A-Z]{1,2}(-|$)/.test(c)) return true;

  // A rack word at the front, then a separator, or a number straight after it.
  // BAY-1, BAY 1, BAY1, SHELF-D, TUB-02, RACK-A1.
  //
  // NOT a rack word followed by more letters. The first version took an
  // optional separator and then any letter, so BAYSIDE-STORE, ROWING-MACHINE
  // and TUBE-CUTTER were all refused as racking. Those are real places, and a
  // form that refuses them is a form somebody stops trusting.
  return RACK_WORDS.some((w) => new RegExp(`^${w}([- _][A-Z0-9]|\\d)`).test(c));
}

/**
 * What to say when the free-text form is handed a rack position.
 *
 * One sentence of what is wrong, one of what to do instead. Guy reads these,
 * not a developer.
 */
export const RACK_CODE_REFUSAL =
  'That looks like a place on the racking. Use "Add a row of racking" instead, '
  + 'which builds the code from the building, row, bay, shelf and tub, and adds '
  + 'only the ones that are missing. This form is for a place that is not on the '
  + 'racking, such as a packing bench.';
